import { graphql } from 'gatsby';
import React from 'react';
import isNil from 'utils/isNil';
import styled from 'styled-components';
import ToolboxLayout from 'layouts/toolboxLayout';
import PodcastHeroSection from 'components/Page/Podcast/HeroSection';
import PodcastAboutShowSection from 'components/Page/Podcast/AboutShowSection';
import StickyMobileForm from 'components/Form/StickyMobileForm';
import ContentfulCardsBlock from 'components/Contentful/ContentfulCardsBlock';
import ContentfulToggle from 'components/Contentful/ContentfulToggle';
import usePodcastLatestEpisode from 'hooks/usePodcastLatestEpisode';
import usePodcastEpisodes from 'hooks/usePodcastEpisodes';
import { truncateText } from 'utils/text';
import podcastIcon from 'images/podcast-black.svg';
import BookmarkContext from 'utils/BookmarkContext';
import useDefaultSubscribeForm from 'hooks/useDefaultSubscribeForm';
import getBreakpointView from 'utils/getBreakpointView';

const PodcastPage = props => {
  const { allContentfulImage: pageImages } = props.data;
  const subscribeForm = useDefaultSubscribeForm();
  const isMobile = getBreakpointView(['xs']);

  let backgroundImg;
  let favSpotify;
  let favPodcast;
  let favGooglePodcast;
  const latestEpisode = usePodcastLatestEpisode();

  pageImages.edges.forEach(imageItem => {
    if (imageItem.node.internalName === 'bg-main-white-blue') {
      backgroundImg = !isNil(imageItem.node.image) ? imageItem.node.image.file.url : '';
    } else if (imageItem.node.internalName === 'podcast-favorite-spotify') {
      favSpotify = !isNil(imageItem.node.image) ? imageItem.node.image.file.url : '';
    } else if (imageItem.node.internalName === 'podcast-favorite-podcast') {
      favPodcast = !isNil(imageItem.node.image) ? imageItem.node.image.file.url : '';
    } else if (imageItem.node.internalName === 'podcast-favorite-google-podcast') {
      favGooglePodcast = !isNil(imageItem.node.image) ? imageItem.node.image.file.url : '';
    }
  });

  const favorites = [
    {
      title: 'Spotify',
      logo: favSpotify,
      link:
        latestEpisode.spotifyLink ||
        'https://open.spotify.com/show/76wMZ1SJV4OAql9HzbnQ87?si=66s37hbqRpu_4vQCaB1T3w',
    },
    {
      title: 'Podcast',
      logo: favPodcast,
      link:
        latestEpisode.applePodcastsLink ||
        'https://podcasts.apple.com/us/podcast/toolbox-for-the-trades/id1503078938',
    },
    {
      title: 'Google Podcast',
      logo: favGooglePodcast,
      link:
        latestEpisode.googlePodcastLink ||
        'https://podcasts.google.com/feed/aHR0cHM6Ly90b29sYm94Zm9ydGhldHJhZGVzLmxpYnN5bi5jb20vcnNz/episode/MGY3MzYyNjAtZWFiNy00OTdiLWE3ZmUtYzEyMjk1YzNkMjU0?ved=0CBEQzsICahcKEwigvfW3k7DpAhUAAAAAHQAAAAAQAQ',
    },
  ];

  const moreEpisodes = usePodcastEpisodes();

  const data = moreEpisodes
    ? Array.isArray(moreEpisodes)
      ? moreEpisodes
      : [moreEpisodes]
    : moreEpisodes;

  const podcastSeasons = data.reduce((seasonsData, { node }) => {
    const newData = { ...seasonsData };
    if (!newData[node.season]) {
      newData[node.season] = [];
    }
    newData[node.season].push(node);

    return newData;
  }, {});

  const seasonTitles = Object.keys(podcastSeasons).map(season => `Season ${season}`);
  const podcastCards = Object.keys(podcastSeasons).map(season => {
    const seasonData = podcastSeasons[season];

    return {
      description: '',
      bgColor: 'Alice Blue',
      cardType: 'Image',
      bodyType: 'simple',
      cards: seasonData
        ? seasonData.map(episode => {
            const podcastExcerpt =
              episode.podExcerpt?.podExcerpt || episode.excerpt?.content[0]?.content[0]?.value;
            const descriptionTrunc = truncateText(podcastExcerpt, 90);
            return {
              subTitle: `${episode.taxonomy.topics.join(' • ')} • ${episode.minutes} minutes`,
              title: episode.podcastTitle,
              description: {
                description: descriptionTrunc,
              },
              cover: !isNil(episode.cardPhoto?.image)
                ? episode.cardPhoto?.image
                : episode.coverPhoto?.image,
              ctaLink: episode.seo.pageUrl,
              markIcon: podcastIcon,
              image: episode?.cardPhoto?.image?.file?.url,
            };
          })
        : [],
    };
  });

  const breadcrumbs = {
    data: [
      {
        title: 'Toolbox',
        icon: null,
        url: '/toolbox',
      },
      !isMobile && {
        title: 'Podcast',
        icon: podcastIcon,
        url: '',
      },
    ],
    absolute: true,
  };

  return (
    <ToolboxLayout breadcrumbs={breadcrumbs} contentfulSeo={props.data.contentfulSeo} {...props}>
      <Container>
        <PodcastHeroSection
          backgroundImg={backgroundImg}
          subTitle={`${latestEpisode?.taxonomy.topics.join(' • ')} • ${
            latestEpisode?.minutes
          } minutes`}
          title={latestEpisode?.podcastTitle}
          description={truncateText(latestEpisode?.excerpt?.content[0]?.content[0]?.value, 125)}
          coverPhoto={latestEpisode?.coverPhoto}
          detailLink={latestEpisode?.seo.pageUrl}
          episodeId={latestEpisode?.episodeId}
          favorites={favorites}
        />
        <BookmarkContext.Provider value={{ type: 'podcast' }}>
          <ContentfulToggle
            defaultValue={seasonTitles[seasonTitles.length - 1]}
            staticContent
            items={podcastCards.map((season, index) => {
              return <ContentfulCardsBlock cols={3} key={`block${index}`} {...season} />;
            })}
            titles={seasonTitles}
            backgroundColor="var(--blue-fourth)"
            type="dropdown"
            startingIndex={seasonTitles.length - 1}
          />
        </BookmarkContext.Provider>

        {latestEpisode.aboutTheShow && (
          <PodcastAboutShowSection content={latestEpisode?.aboutTheShow} />
        )}
        <StickyMobileForm inlineMarketingForm={subscribeForm} />
      </Container>
    </ToolboxLayout>
  );
};

export default PodcastPage;

export const PodcastPageQuery = graphql`
  query PodcastPageQuery {
    allContentfulImage(
      filter: {
        contentful_id: {
          in: [
            "1QunGXQEjMCRHTpAnOlkjb" #bg-main-white-blue
            "3c6QsgzhIBl3kbaz7o9SDJ" #podcast-favorite-spotify
            "2tJ0HouccbJrmiZ5Vi4wOK" #podcast-favorite-podcast
            "5xdGqxVtDHkgnxH90iMUTL" #podcast-favorite-google-podcast
          ]
        }
      }
    ) {
      edges {
        node {
          contentful_id
          internalName
          alt
          image {
            file {
              url
            }
            fluid(quality: 100) {
              ...GatsbyContentfulFluid_withWebp_noBase64
            }
          }
        }
      }
    }
    contentfulSeo(contentful_id: { eq: "3Cb3N5BWNaBItN6srJOoms" }) {
      ...Seo
    }
  }
`;

export const Container = styled.div`
  .section-layout--inner-container {
    padding-top: 0px;
  }

  @media (max-width: 576px) {
    .dropdown-select--container {
      padding: 0 20px;
    }
  }
`;
